<template>
    <div class="class-1">
        <!-- Header -->
        <AppHeader title="Shifts"/>

        <!-- Shifts Section -->
        <div class="mt-4">
            <div class="row">
                <div class="col-12">
                    <div class="text-right mb-3">
                        <button class="add-shift-btn" @click="showAddShiftModal = true">
                            <i class="fas fa-plus-circle"></i> Create New Shift
                        </button>
                    </div>
                    
                    <!-- Filter Section -->
                    <div class="filter-section mb-3" style="margin-left: 1rem;">
                        <label for="statusFilter">Filter by Status:</label>
                        <select id="statusFilter" v-model="selectedStatus" @change="filterByStatus" class="form-control">
                            <option value="">All Shifts</option>
                            <option value="open">Open Shifts</option>
                            <option value="closed">Closed Shifts</option>
                        </select>
                    </div>

                    <div class="filter-section mb-3" style="margin-left: 1rem;">
                        <label for="cashierFilter">Filter by Cashier:</label>
                        <select id="cashierFilter" v-model="selectedCashier" @change="filterByCashier" class="form-control">
                            <option value="">All Cashiers</option>
                            <option v-for="cashier in cashiers" :key="cashier.id" :value="cashier.id">
                                {{ cashier.name }}
                            </option>
                        </select>
                    </div>

                    <div v-if="loading" class="text-center">Loading shifts...</div>
                    <div v-else>
                        <!-- Shifts Table -->
                        <table>
                            <thead>
                                <tr>
                                    <th>Cashier</th>
                                    <th>Open Date & Time</th>
                                    <th>Close Date & Time</th>
                                    <th>Status</th>
                                    <th>Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="shift in filteredShifts" :key="shift.id" class="action-buttons">
                                    <td>{{ getCashierName(shift.cashier) }}</td>
                                    <td>{{ formatDateTime(shift.open_datetime) }}</td>
                                    <td>{{ shift.close_datetime ? formatDateTime(shift.close_datetime) : '—' }}</td>
                                    <td>
                                        <span :class="['status-badge', shift.close_datetime ? 'status-closed' : 'status-open']">
                                            <i :class="shift.close_datetime ? 'fas fa-lock' : 'fas fa-lock-open'"></i>
                                            {{ shift.close_datetime ? 'Closed' : 'Open' }}
                                        </span>
                                    </td>
                                    <td>{{ shift.close_datetime ? calculateDuration(shift.open_datetime, shift.close_datetime) : '—' }}</td>
                                   
                                </tr>
                                <tr v-if="filteredShifts.length === 0">
                                    <td colspan="6" class="text-center">No shifts found</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add Shift Modal -->
        <div v-if="showAddShiftModal" class="modal-overlay" @click="showAddShiftModal = false">
            <div class="modal-container" @click.stop>
                <div class="modal-header">
                    <h3><i class="fas fa-plus-circle"></i> Create New Shift</h3>
                    <button class="close-btn" @click="showAddShiftModal = false">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="modal-body">
                    <!-- Create Shift Form -->
                    <form @submit.prevent="handleCreateShift">
                        <!-- Cashier Selection -->
                        <div class="form-row">
                            <div class="form-group col-md-12">
                                <label for="modal-cashier">
                                    <i class="fas fa-user"></i> Select Cashier <span class="required">*</span>
                                </label>
                                <select 
                                    class="form-control" 
                                    id="modal-cashier" 
                                    v-model="shiftForm.cashier"
                                    required
                                >
                                    <option value="">-- Select Cashier --</option>
                                    <option v-for="cashier in cashiers" :key="cashier.id" :value="cashier.id">
                                        {{ cashier.name }}
                                    </option>
                                </select>
                                <small class=""><i class="fas fa-info-circle"></i> Select the cashier for this shift</small>
                            </div>
                        </div>

                        <!-- Warning message if cashier has open shift -->
                        <div v-if="cashierHasOpenShift" class="alert alert-warning">
                            <i class="fas fa-exclamation-triangle"></i> ⚠️ This cashier already has an open shift. Please close it before opening a new one.
                        </div>

                        <!-- Buttons -->
                        <div class="form-row">
                            <div class="form-group col-12 d-flex justify-content-end gap-2">
                                <button type="button" class="btn btn-secondary" @click="showAddShiftModal = false">
                                    <i class="fas fa-times"></i> Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    class="btn btn-primary" 
                                    :disabled="submitting || cashierHasOpenShift"
                                >
                                    <i :class="submitting ? 'fas fa-spinner fa-spin' : 'fas fa-play'"></i>
                                    {{ submitting ? 'Opening...' : 'Open Shift' }}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- View Shift Details Modal -->
        <div v-if="showViewShiftModal" class="modal-overlay" @click="showViewShiftModal = false">
            <div class="modal-container modal-lg" @click.stop>
                <div class="modal-header">
                    <h3><i class="fas fa-info-circle"></i> Shift Details</h3>
                    <button class="close-btn" @click="showViewShiftModal = false">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <div class="modal-body">
                    <div v-if="selectedShift" class="shift-details">
                        <div class="detail-row">
                            <strong><i class="fas fa-user"></i> Cashier:</strong> {{ getCashierName(selectedShift.cashier) }}
                        </div>
                        <div class="detail-row">
                            <strong><i class="fas fa-calendar-alt"></i> Open Date & Time:</strong> {{ formatDateTime(selectedShift.open_datetime) }}
                        </div>
                        <div class="detail-row">
                            <strong><i class="fas fa-calendar-check"></i> Close Date & Time:</strong> 
                            <span v-if="selectedShift.close_datetime">
                                {{ formatDateTime(selectedShift.close_datetime) }}
                            </span>
                            <span v-else class="text-muted">Not closed yet</span>
                        </div>
                        <div class="detail-row">
                            <strong><i class="fas fa-tag"></i> Status:</strong> 
                            <span :class="['status-badge', selectedShift.close_datetime ? 'status-closed' : 'status-open']">
                                <i :class="selectedShift.close_datetime ? 'fas fa-lock' : 'fas fa-lock-open'"></i>
                                {{ selectedShift.close_datetime ? 'Closed' : 'Open' }}
                            </span>
                        </div>
                        <div class="detail-row" v-if="selectedShift.close_datetime">
                            <strong><i class="fas fa-clock"></i> Duration:</strong> {{ calculateDuration(selectedShift.open_datetime, selectedShift.close_datetime) }}
                        </div>
                        
                        <!-- Shift Summary -->
                        <div class="summary-section mt-4">
                            <h4><i class="fas fa-chart-bar"></i> Shift Summary</h4>
                            <div class="summary-grid">
                                <div class="summary-item">
                                    <span class="summary-label"><i class="fas fa-shopping-cart"></i> Total Orders:</span>
                                    <span class="summary-value">{{ selectedShift.total_orders || 0 }}</span>
                                </div>
                                <div class="summary-item">
                                    <span class="summary-label"><i class="fas fa-dollar-sign"></i> Total Sales:</span>
                                    <span class="summary-value">{{ formatCurrency(selectedShift.total_sales || 0) }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            shifts: [],
            cashiers: [],
            loading: true,
            selectedStatus: '',
            selectedCashier: '',
            filteredShifts: [],
            currentDate: '',
            currentTime: '',
            showAddShiftModal: false,
            showViewShiftModal: false,
            submitting: false,
            selectedShift: null,
            shiftForm: {
                cashier: '',
            },
        };
    },
    mounted() {
        this.updateDateTime();
        this.startDateTimeUpdate();
        this.fetchShifts();
        this.fetchCashiers();
    },
    computed: {
        cashierHasOpenShift() {
            if (!this.shiftForm.cashier) return false;
            
            // Check if selected cashier already has an open shift
            return this.shifts.some(shift => 
                shift.cashier == this.shiftForm.cashier && !shift.close_datetime
            );
        }
    },
    methods: {
        async fetchShifts() {
            try {
                this.$root.isLoading = true;
                this.loading = true;
                const token = localStorage.getItem('authToken');
                const appId = localStorage.getItem('appId') || 1;
                
                const response = await fetch(`${this.$BASE_API_URL}/api/shifts?appId=${appId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch shifts');
                }

                const data = await response.json();
                this.shifts = data.data.results || [];
                this.filteredShifts = this.shifts;
            } catch (error) {
                console.error('Error fetching shifts:', error);
                this.$toast.error('Failed to load shifts. Please try again.');
            } finally {
                this.$root.isLoading = false;
                this.loading = false;
            }
        },

        async fetchCashiers() {
            try {
                const token = localStorage.getItem('authToken');
                const appId = localStorage.getItem('appId') || 1;
                
                const response = await fetch(`${this.$BASE_API_URL}/api/users?appId=${appId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch cashiers');
                }

                const data = await response.json();
                // Filter users who are cashiers or format the user list
                this.cashiers = (data.data || []).map(user => ({
                    id: user.id,
                    name: user.name || user.username || `User ${user.id}`
                }));
            } catch (error) {
                console.error('Error fetching cashiers:', error);
            }
        },

        filterByStatus() {
            this.applyFilters();
        },

        filterByCashier() {
            this.applyFilters();
        },

        applyFilters() {
            let filtered = this.shifts;

            // Filter by status
            if (this.selectedStatus) {
                if (this.selectedStatus === 'open') {
                    filtered = filtered.filter(shift => !shift.close_datetime);
                } else if (this.selectedStatus === 'closed') {
                    filtered = filtered.filter(shift => shift.close_datetime);
                }
            }

            // Filter by cashier
            if (this.selectedCashier) {
                filtered = filtered.filter(shift => shift.cashier == this.selectedCashier);
            }

            this.filteredShifts = filtered;
        },

        getCashierName(cashierId) {
            if (!cashierId) return 'Unknown';
            const cashier = this.cashiers.find(c => c.id === cashierId);
            return cashier ? cashier.name : `Cashier #${cashierId}`;
        },

        formatDateTime(dateTime) {
            if (!dateTime) return '';
            const date = new Date(dateTime);
            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        },

        calculateDuration(openDateTime, closeDateTime) {
            const open = new Date(openDateTime);
            const close = new Date(closeDateTime);
            const durationMs = close - open;
            const hours = Math.floor(durationMs / (1000 * 60 * 60));
            const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
            return `${hours}h ${minutes}m`;
        },

        formatCurrency(amount) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2
            }).format(amount);
        },

        async handleCreateShift() {
            if (!this.shiftForm.cashier) {
                this.$toast.error('Please select a cashier');
                return;
            }

            if (this.cashierHasOpenShift) {
                this.$toast.error('This cashier already has an open shift. Please close it first.');
                return;
            }

            this.submitting = true;
            
            try {
                this.$root.isLoading = true;
                const token = localStorage.getItem('authToken');
                const appId = localStorage.getItem('appId') || 1;

                const payload = {
                    appId: parseInt(appId),
                    cashier: parseInt(this.shiftForm.cashier)
                };

                const response = await fetch(`${this.$BASE_API_URL}/api/shifts`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });

                const data = await response.json();

                if (response.ok) {
                    this.$toast.success('Shift opened successfully!');
                    this.showAddShiftModal = false;
                    this.shiftForm.cashier = '';
                    this.fetchShifts(); // Refresh the list
                } else {
                    if (data.message && data.message.includes('already has an open shift')) {
                        this.$toast.error('This cashier already has an open shift');
                    } else {
                        this.$toast.error(data.message || 'Failed to open shift');
                    }
                    
                    // Handle validation errors
                    if (data.errors) {
                        Object.values(data.errors).forEach(error => {
                            this.$toast.error(error[0]);
                        });
                    }
                }
            } catch (error) {
                console.error('Error:', error);
                this.$toast.error('An error occurred. Please try again.');
            } finally {
                this.$root.isLoading = false;
                this.submitting = false;
            }
        },

        async closeShift(shift) {
            if (confirm(`Are you sure you want to close the shift for ${this.getCashierName(shift.cashier)}?`)) {
                try {
                    const token = localStorage.getItem('authToken');
                    const appId = localStorage.getItem('appId') || 1;
                    
                    const response = await fetch(`${this.$BASE_API_URL}/api/shifts/${shift.id}/close?appId=${appId}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    const data = await response.json();

                    if (response.ok) {
                        this.$toast.success('Shift closed successfully!');
                        this.fetchShifts();
                    } else {
                        this.$toast.error(data.message || 'Failed to close shift');
                    }
                } catch (error) {
                    console.error('Error closing shift:', error);
                    this.$toast.error('An error occurred. Please try again.');
                }
            }
        },

        viewShiftDetails(shift) {
            this.selectedShift = shift;
            this.showViewShiftModal = true;
        },

        updateDateTime() {
            const now = new Date();
            this.currentDate = now.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
            this.currentTime = now.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
            });
        },

        startDateTimeUpdate() {
            setInterval(() => {
                this.updateDateTime();
            }, 1000);
        },
    },
    watch: {
        shifts: {
            handler() {
                this.applyFilters();
            },
            deep: true
        }
    }
};
</script>

<style scoped>
.container {
    max-width: 800px;
    margin: 0 auto;
}

.filter-section {
    max-width: 300px;
    margin-bottom: 1rem;
}

.filter-section label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
}

.action-buttons button {
    background: none;
    border: none;
    cursor: pointer;
    margin: 0 5px;
    position: relative;
    transition: transform 0.2s ease, opacity 0.2s ease;
}

.action-buttons button:hover {
    transform: scale(1.2);
    opacity: 0.8;
}

.action-buttons button:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: -25px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    padding: 5px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
}

.icon-btn {
    font-size: 1.2rem;
    padding: 5px;
}

.icon-btn i {
    font-size: 1.2rem;
}

.add-shift-btn {
    padding: 10px 15px;
    background-color: #4caf50;
    border: none;
    cursor: pointer;
    width: max-content;
    border-radius: 5px;
    margin-right: 1rem;
    color: white;
    font-weight: 500;
}

.add-shift-btn i {
    margin-right: 5px;
}

.add-shift-btn:hover {
    background-color: #45a049;
}

.form-control {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
}

table {
    width: 100%;
    border-collapse: collapse;
}

th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
}

th {
    font-weight: 600;
}

.text-center {
    text-align: center;
}

.mb-3 {
    margin-bottom: 1rem;
}

.mt-4 {
    margin-top: 1.5rem;
}

/* Status Badge Styles */
.status-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    text-align: center;
}

.status-badge i {
    margin-right: 4px;
    font-size: 11px;
}

.status-open {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.status-closed {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

/* Alert Styles */
.alert {
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 1rem;
}

.alert i {
    margin-right: 5px;
}

.alert-warning {
    background-color: #fff3cd;
    border: 1px solid #ffeeba;
    color: #856404;
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-container {
    background-color: var(--bg-color);
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.modal-lg {
    max-width: 700px;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #dee2e6;
    border-radius: 8px 8px 0 0;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.25rem;
}

.modal-header h3 i {
    margin-right: 8px;
    color: #4caf50;
}

.close-btn {
    background: none;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
    color: #6c757d;
}

.close-btn:hover {
    color: #343a40;
}

.modal-body {
    padding: 1.5rem;
}

.gap-2 {
    gap: 0.5rem;
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s ease;
}

.btn i {
    margin-right: 5px;
}

.btn-primary {
    background-color: #4caf50;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background-color: #45a049;
}

.btn-primary:disabled {
    background-color: #a5d6a7;
    cursor: not-allowed;
}

.btn-secondary {
    background-color: #6c757d;
    color: white;
}

.btn-secondary:hover {
    background-color: #5a6268;
}

.required {
    color: red;
}

/* Shift Details Styles */
.shift-details {
    padding: 0.5rem;
}

.detail-row {
    margin-bottom: 1rem;
    padding: 0.5rem;
    border-bottom: 1px solid #eee;
}

.detail-row strong {
    display: inline-block;
    min-width: 140px;
    color: #555;
}

.detail-row strong i {
    margin-right: 5px;
    color: #4caf50;
    width: 18px;
}

.text-muted {
    color: #6c757d;
    font-style: italic;
}

.summary-section {
    background-color: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    margin-top: 1rem;
}

.summary-section h4 {
    margin-top: 0;
    margin-bottom: 1rem;
    color: #333;
}

.summary-section h4 i {
    margin-right: 5px;
    color: #4caf50;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
}

.summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.summary-label {
    font-weight: 500;
    color: #555;
}

.summary-label i {
    margin-right: 5px;
    color: #4caf50;
}

.summary-value {
    font-weight: 600;
    color: #333;
}
</style>